import React, { useEffect, useState } from "react";
import { Alert, Box, Chip, Stack, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { getCustomer360, Customer360 } from "../../services/supportService";

/** Phase 2 §5.6: who the customer is, the ride this ticket is about, recent rides and past tickets — on one card. */
const when = (iso?: string | null) => (iso ? new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short" }) : "—");

const Customer360Card: React.FC<{ ticketId: string | number }> = ({ ticketId }) => {
    const navigate = useNavigate();
    const [data, setData] = useState<Customer360 | null>(null);
    const [error, setError] = useState(false);
    useEffect(() => {
        getCustomer360(ticketId).then((r) => setData(r.data)).catch(() => setError(true));
    }, [ticketId]);

    if (error) return <Alert severity="info" sx={{ mt: 2 }}>Customer card unavailable.</Alert>;
    if (!data) return null;
    const c = data.customer;
    return (
        <Box sx={{ mt: 2, p: 1.5, border: "1px solid", borderColor: "divider", borderRadius: 2 }} data-testid="customer-360">
            <Typography variant="caption" sx={{ fontWeight: 700 }}>CUSTOMER</Typography>
            <Typography variant="body2">
                {c.name || "Unnamed"} · {c.kind}{c.city ? ` · ${c.city}` : ""}{c.language ? ` · ${c.language}` : ""}
                {c.accountAgeDays != null ? ` · customer for ${c.accountAgeDays} day(s)` : ""}
            </Typography>
            {data.driver && (
                <Typography variant="body2">
                    Driver: {data.driver.status} · rating {data.driver.rating} · documents {data.driver.documentsVerified ? "verified" : "NOT verified"}
                </Typography>
            )}
            {data.ride && (
                <Box sx={{ mt: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>Ride #{data.ride.id} · {data.ride.status} · ₹{data.ride.totalFare ?? "—"}</Typography>
                    <Typography variant="caption" display="block">{data.ride.pickupLocation} → {data.ride.dropoffLocation}</Typography>
                    <Typography variant="caption" display="block">Payment: {data.ride.paymentMethod ?? "—"} {data.ride.paymentStatus ?? ""}</Typography>
                    {data.ride.fraud && <Chip size="small" color="error" sx={{ mt: 0.5 }} label={`Fraud flag ${data.ride.fraud.fraudPct}% · ${data.ride.fraud.status}`} />}
                </Box>
            )}
            {data.recentRides.length > 0 && (
                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                    Last rides: {data.recentRides.map((r) => `#${r.id} ${r.status} ₹${r.totalFare ?? "—"} (${when(r.requestedAt)})`).join(" · ")}
                </Typography>
            )}
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 1 }} alignItems="center">
                <Typography variant="caption">Past tickets ({data.tickets.length}){data.csat.average != null ? ` · avg rating ${data.csat.average}/5` : ""}:</Typography>
                {data.tickets.map((t) => (
                    <Chip key={t.id} size="small" variant="outlined" clickable onClick={() => navigate(`/support-ticket/${t.id}`)}
                        label={`${t.publicNumber || `#${t.id}`} · ${t.status}${t.csatScore != null ? ` · ${t.csatScore}★` : ""}`} />
                ))}
            </Stack>
        </Box>
    );
};

export default Customer360Card;
